import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';

const SEAL_SELECTORS =
  '.sealsubs-target-element, .sealsubs-full, .sealsubs-container, .sls-more-info-container';

const DEFAULT_CURRENCY_DECIMALS = 2;

/** Spanish Seal copy → i18n keys (Seal storefront strings are Spanish-only). */
const SEAL_SPANISH_PHRASES = [
  ['Suscripción Bimensual', 'planBimonthly'],
  ['Suscripción Mensual', 'planMonthly'],
  ['Cancela la suscripción en cualquier momento', 'cancelAnytime'],
  ['Cancela cuando quieras', 'cancelAnytime'],
  ['Envíos gratis', 'freeShipping'],
  ['Ahorra en tus futuros pedidos', 'saveFuture'],
  ['Invierte en tu salud a largo plazo', 'investHealth'],
];

const SEAL_SPANISH_UNITS = {
  dia: 'day',
  día: 'day',
  dias: 'days',
  días: 'days',
  semana: 'week',
  semanas: 'weeks',
  mes: 'month',
  meses: 'months',
};

/**
 * @param {HTMLElement} root
 * @returns {Record<string, string> | null}
 */
function readSealI18n(root) {
  const script = root.querySelector('[ref="sealI18n"]');
  if (!(script instanceof HTMLScriptElement)) return null;

  try {
    return /** @type {Record<string, string>} */ (JSON.parse(script.textContent || '{}'));
  } catch {
    return null;
  }
}

/**
 * @param {string} text
 * @param {Record<string, string> | null | undefined} i18n
 * @returns {string}
 */
function localizeSealInterval(text, i18n) {
  if (!text || !i18n) return text;

  return text.replace(
    /(\d+)\s*(días?|dias?|semanas?|meses?)/gi,
    (_, count, unit) => {
      const key = SEAL_SPANISH_UNITS[unit.toLowerCase()];
      const translatedUnit = key && i18n[key] ? i18n[key] : unit;
      return `${count} ${translatedUnit}`;
    }
  );
}

/**
 * @param {string} text
 * @param {Record<string, string> | null | undefined} i18n
 * @returns {string}
 */
function localizeSealPhrases(text, i18n) {
  if (!text || !i18n) return text;

  let result = text;

  for (const [spanish, key] of SEAL_SPANISH_PHRASES) {
    const translated = i18n[key];
    if (!translated || translated === spanish) continue;

    const escaped = spanish.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    result = result.replace(new RegExp(escaped + '\\.?', 'gi'), translated);
  }

  if (i18n.deliveryEvery) {
    result = result.replace(/\bEntrega cada\b/gi, i18n.deliveryEvery);
  }

  result = localizeSealInterval(result, i18n);

  return result;
}

/**
 * @param {string} html
 * @param {Record<string, string> | null | undefined} i18n
 * @returns {string}
 */
function localizeSealHtml(html, i18n) {
  if (!html || !i18n || i18n.locale === 'es') return html;
  return localizeSealPhrases(html, i18n);
}

/**
 * @param {number} cents
 * @param {string} template
 * @returns {string}
 */
function formatCentsWithTemplate(cents, template) {
  if (!template) return (cents / 100).toFixed(2);

  return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, placeholder) => {
    if (typeof placeholder !== 'string') return '';

    let thousandsSeparator = ',';
    let decimalSeparator = '.';
    let precision = DEFAULT_CURRENCY_DECIMALS;

    if (placeholder === 'amount_no_decimals') {
      precision = 0;
    } else if (placeholder === 'amount_with_comma_separator') {
      thousandsSeparator = '.';
      decimalSeparator = ',';
    } else if (placeholder === 'amount_no_decimals_with_comma_separator') {
      thousandsSeparator = '.';
      precision = 0;
    } else if (placeholder === 'amount_with_space_separator') {
      thousandsSeparator = ' ';
      decimalSeparator = ',';
    } else if (placeholder === 'amount_with_period_and_space_separator') {
      thousandsSeparator = ' ';
      decimalSeparator = '.';
    } else if (placeholder === 'amount_with_apostrophe_separator') {
      thousandsSeparator = "'";
      decimalSeparator = '.';
    }

    const roundedNumber = (cents / 100).toFixed(precision);
    let [whole, fraction = ''] = roundedNumber.split('.');
    whole = whole.replace(/\d(?=(\d\d\d)+(?!\d))/g, (digit) => digit + thousandsSeparator);

    return precision <= 0 ? whole : `${whole}${decimalSeparator}${fraction.padEnd(precision, '0')}`;
  });
}

/**
 * @param {string} html
 * @returns {string}
 */
function sanitizeSubscriptionDescription(html) {
  if (!html) return '';

  const template = document.createElement('template');
  template.innerHTML = html;

  for (const node of template.content.querySelectorAll('p')) {
    const text = (node.textContent || '').toLowerCase();
    const inner = node.innerHTML.toLowerCase();

    if (
      text.includes('programa de puntos') ||
      text.includes('términos y condiciones') ||
      text.includes('terminos y condiciones') ||
      inner.includes('terminos-y-condiciones') ||
      inner.includes('/account/register')
    ) {
      node.remove();
    }
  }

  return template.innerHTML.trim();
}

/**
 * @param {Record<string, unknown>} group
 * @param {Record<string, unknown>} plan
 * @param {Record<string, string> | null | undefined} [i18n]
 * @returns {string}
 */
function getDeliveryBenefitText(group, plan, i18n) {
  const groupOptions = /** @type {Array<Record<string, string>> | undefined} */ (group.options);
  const planOptions = /** @type {Array<Record<string, string>> | undefined} */ (plan.options);
  const rawLabel = groupOptions?.[0]?.name || i18n?.deliveryEvery || 'Entrega cada';
  const label = localizeSealPhrases(rawLabel, i18n);
  const interval = localizeSealInterval(planOptions?.[0]?.value || '', i18n);

  return `${label} ${interval}`.trim();
}

/**
 * @param {ParentNode | null | undefined} scope
 * @param {boolean} visible
 */
function setShopifyBuyerConsentVisible(scope, visible) {
  const roots = [scope, document].filter(Boolean);

  for (const root of roots) {
    if (!root || typeof /** @type {{ querySelectorAll?: unknown }} */ (root).querySelectorAll !== 'function') {
      continue;
    }

    for (const consent of /** @type {ParentNode} */ (root).querySelectorAll(
      '#shopify-buyer-consent, [data-consent-type="subscription"]'
    )) {
      if (!(consent instanceof HTMLElement)) continue;

      if (visible) {
        consent.hidden = false;
        consent.removeAttribute('aria-hidden');
        consent.classList.remove('hidden');
        consent.style.removeProperty('display');
      } else {
        consent.hidden = true;
        consent.setAttribute('aria-hidden', 'true');
        consent.classList.add('hidden');
        consent.style.display = 'none';
      }
    }
  }
}

/**
 * @param {ParentNode | null | undefined} scope
 */
function hideShopifyBuyerConsent(scope) {
  setShopifyBuyerConsentVisible(scope, false);
}

/**
 * @param {ParentNode | null | undefined} scope
 * @returns {Record<string, unknown> | null}
 */
function readSealProductData(scope) {
  const el = scope?.querySelector('.sealsubs-target-element[data-product]');
  if (!(el instanceof HTMLElement) || !el.dataset.product) return null;

  try {
    return JSON.parse(el.dataset.product);
  } catch {
    return null;
  }
}

/**
 * @param {Record<string, unknown> | null | undefined} sealData
 * @param {string | undefined} planId
 */
function findSealPlan(sealData, planId) {
  if (!sealData || !planId) return null;

  const groups = /** @type {Array<Record<string, unknown>> | undefined} */ (sealData.selling_plan_groups);
  if (!groups?.length) return null;

  for (const group of groups) {
    const plans = /** @type {Array<Record<string, unknown>> | undefined} */ (group.selling_plans);
    if (!plans?.length) continue;

    for (const plan of plans) {
      if (String(plan.id) === String(planId)) {
        return { plan, group };
      }
    }
  }

  return null;
}

/**
 * @param {ParentNode | null | undefined} scope
 */
function hideSealSubscriptions(scope) {
  if (!scope) return;

  for (const node of scope.querySelectorAll(SEAL_SELECTORS)) {
    if (node instanceof HTMLElement) {
      node.setAttribute('hidden', '');
      node.setAttribute('aria-hidden', 'true');
      node.style.display = 'none';
    }
  }

  const form = scope.querySelector?.('form.shopify-product-form');
  if (!(form instanceof HTMLFormElement)) return;

  for (const input of form.querySelectorAll('input[name="selling_plan"]:not([data-theme-selling-plan])')) {
    input.remove();
  }

  for (const input of form.querySelectorAll('input[name^="subs_type_"], select[name="subs_interval"]')) {
    input.remove();
  }
}

/**
 * Keeps Seal Subscriptions widget hidden when the theme owns purchase UI.
 */
class SealSubscriptionsGuard extends HTMLElement {
  #abortController = new AbortController();

  connectedCallback() {
    const { signal } = this.#abortController;
    const scope = this.closest('.product-details, [data-testid="product-information-details"], .shopify-section');

    const run = () => {
      hideSealSubscriptions(scope);
      hideSealSubscriptions(this.closest('.buy-buttons-block'));
    };

    run();
    requestAnimationFrame(run);
    window.setTimeout(run, 300);
    window.setTimeout(run, 1500);

    const form = scope?.querySelector('form.shopify-product-form');
    if (form) {
      const observer = new MutationObserver(run);
      observer.observe(form, { childList: true, subtree: true });
      signal.addEventListener('abort', () => observer.disconnect());
    }
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }
}

/**
 * @typedef {Object} VariantPricingOption
 * @property {number} price
 * @property {number} compareAt
 */

/**
 * @typedef {Object} VariantSubscriptionPricing
 * @property {number} price
 * @property {number} compareAt
 * @property {boolean} available
 * @property {number} variantId
 */

/**
 * @typedef {Object} VariantPricingEntry
 * @property {VariantPricingOption} oneTime
 * @property {VariantSubscriptionPricing} subscription
 */

/**
 * Syncs custom purchase option radios with the product form, hides Seal Subscriptions UI,
 * and switches variant / selling plan for subscription vs one-time purchase.
 */
class PurchaseOptionsComponent extends HTMLElement {
  #abortController = new AbortController();
  /** @type {string | undefined} */
  #pickerVariantId;
  #sealCleanupScheduled = false;

  connectedCallback() {
    const { signal } = this.#abortController;
    const section = this.closest('.shopify-section, dialog');

    section?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    this.addEventListener('change', this.#onChange, { signal });

    const deliverySelect = this.querySelector('[ref="deliverySelect"]');
    deliverySelect?.addEventListener('change', this.#onDeliveryChange, { signal });

    const form = this.#getForm();
    form?.addEventListener('submit', this.#onFormSubmit, { capture: true, signal });

    const productForm = this.#getProductForm();
    productForm?.addEventListener('click', this.#onAddToCartClick, { capture: true, signal });
    productForm?.addEventListener('click', this.#onAcceleratedCheckoutClick, { capture: true, signal });

    this.#pickerVariantId = this.dataset.purchaseVariantId;
    this.#scheduleSealCleanup();
    this.#syncSealSubscriptionMeta();
    this.#sanitizeRenderedBenefits();
    this.#localizeSealBenefits();
    this.#updatePricesForVariantId(this.#pickerVariantId);
    this.#syncFormState();
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }

  /** @public — used by product-form before cart/add */
  syncFormState() {
    this.#syncFormState({ forAcceleratedCheckout: false });
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const container = this.closest('.product-details, [data-testid="product-information-details"]');
    const productForm =
      container?.querySelector(`product-form-component[data-product-id="${this.dataset.productId}"]`) ??
      container?.querySelector('product-form-component');

    return productForm ?? this.closest('.shopify-section, dialog')?.querySelector('product-form-component');
  }

  /**
   * @returns {HTMLFormElement | null}
   */
  #getForm() {
    return this.#getProductForm()?.querySelector('form.shopify-product-form') ?? null;
  }

  /**
   * @returns {HTMLInputElement | null}
   */
  #getSellingPlanInput() {
    const form = this.#getForm();
    if (!form) return null;

    let themeInput = form.querySelector('input[data-theme-selling-plan]');
    if (!(themeInput instanceof HTMLInputElement)) {
      themeInput = document.createElement('input');
      themeInput.type = 'hidden';
      themeInput.setAttribute('data-theme-selling-plan', '');
      form.prepend(themeInput);
    }

    return themeInput;
  }

  /**
   * @returns {HTMLInputElement | null}
   */
  #getVariantInput() {
    return this.#getForm()?.querySelector('input[name="id"]') ?? null;
  }

  /**
   * @returns {string}
   */
  #getMoneyTemplate() {
    const template = this.querySelector('template[ref="moneyFormat"]');
    return template?.content.textContent?.trim() || '{{amount}}';
  }

  /**
   * @param {number} cents
   * @returns {string}
   */
  #formatCents(cents) {
    return formatCentsWithTemplate(cents, this.#getMoneyTemplate());
  }

  /**
   * @returns {{ variants: Record<string, VariantPricingEntry> } | null}
   */
  #getPricingData() {
    const script = this.querySelector('script[ref="variantPricing"]');
    if (!script?.textContent) return null;

    try {
      return JSON.parse(script.textContent);
    } catch {
      return null;
    }
  }

  /**
   * @param {HTMLElement | null} element
   * @param {number} cents
   */
  #setPriceDisplay(element, cents) {
    if (!(element instanceof HTMLElement)) return;

    if (cents > 0) {
      element.textContent = this.#formatCents(cents);
      element.hidden = false;
    }
  }

  /**
   * @param {HTMLElement | null} element
   * @param {number} compareCents
   * @param {number} priceCents
   */
  #setCompareDisplay(element, compareCents, priceCents) {
    if (!(element instanceof HTMLElement)) return;

    if (compareCents > priceCents && compareCents > 0) {
      element.textContent = this.#formatCents(compareCents);
      element.hidden = false;
    } else {
      element.hidden = true;
    }
  }

  /**
   * @param {boolean} available
   * @param {number | string | undefined} subscriptionVariantId
   */
  #updateSubscriptionAvailability(available, subscriptionVariantId) {
    const subInput = this.querySelector('.purchase-options__input[value="subscription"]');
    const oneTimeInput = this.querySelector('.purchase-options__input[value="one_time"]');

    this.dataset.subscriptionAvailable = String(available);

    if (subscriptionVariantId) {
      this.dataset.subscriptionVariantId = String(subscriptionVariantId);
    }

    if (subInput instanceof HTMLInputElement) {
      subInput.disabled = !available;
      subInput.setAttribute('aria-disabled', available ? 'false' : 'true');

      if (!available && subInput.checked && oneTimeInput instanceof HTMLInputElement) {
        oneTimeInput.checked = true;
        subInput.checked = false;
      }
    }
  }

  /**
   * @param {VariantPricingEntry} variantPricing
   */
  #applyVariantPricing(variantPricing) {
    const { oneTime, subscription } = variantPricing;

    this.#setPriceDisplay(this.querySelector('[ref="oneTimePrice"]'), oneTime.price);
    this.#setCompareDisplay(this.querySelector('[ref="oneTimeComparePrice"]'), oneTime.compareAt, oneTime.price);

    this.#setPriceDisplay(this.querySelector('[ref="subscriptionPrice"]'), subscription.price);
    this.#setCompareDisplay(
      this.querySelector('[ref="subscriptionComparePrice"]'),
      subscription.compareAt,
      subscription.price
    );

    this.#updateSubscriptionAvailability(subscription.available, subscription.variantId);

    const badge = this.querySelector('[ref="subscriptionBadge"]');
    const savings = oneTime.price - subscription.price;
    const savingsTemplate = this.dataset.savingsLabel;

    if (badge instanceof HTMLElement && subscription.available && savings > 0 && savingsTemplate) {
      badge.hidden = false;
      badge.textContent = savingsTemplate.replace('[[amount]]', this.#formatCents(savings));
    } else if (badge instanceof HTMLElement && savings <= 0) {
      badge.hidden = badge.textContent.trim() === '';
    }
  }

  /**
   * @param {string | number | undefined | null} variantId
   */
  #updatePricesForVariantId(variantId) {
    if (!variantId) return;

    const pricing = this.#getPricingData();
    const variantPricing = pricing?.variants?.[String(variantId)];
    if (!variantPricing) return;

    this.#applyVariantPricing(variantPricing);
  }

  #sanitizeRenderedBenefits() {
    const planCopy = this.querySelector('[ref="subscriptionPlanCopy"]');
    if (planCopy instanceof HTMLElement && planCopy.innerHTML.trim()) {
      const cleaned = sanitizeSubscriptionDescription(planCopy.innerHTML)
        .replace(/<\/?p[^>]*>/gi, '')
        .replace(/^\s*<br\s*\/?>/i, '')
        .trim();
      planCopy.innerHTML = cleaned;
    }
  }

  /**
   * Seal plan copy is Spanish-only — map known phrases/intervals to the storefront locale.
   */
  #localizeSealBenefits() {
    const i18n = readSealI18n(this);
    if (!i18n || i18n.locale === 'es') return;

    const titleEl = this.querySelector('[ref="subscriptionTitle"]');
    if (titleEl instanceof HTMLElement && titleEl.textContent) {
      titleEl.textContent = localizeSealPhrases(titleEl.textContent.trim(), i18n);
    }

    const deliveryValue = this.querySelector('[ref="deliveryValue"]');
    if (deliveryValue instanceof HTMLElement && deliveryValue.textContent) {
      deliveryValue.textContent = localizeSealPhrases(deliveryValue.textContent, i18n);
    }

    const deliveryLabel = this.querySelector('.purchase-options__delivery-label');
    if (deliveryLabel instanceof HTMLElement && deliveryLabel.textContent) {
      deliveryLabel.textContent = localizeSealPhrases(deliveryLabel.textContent, i18n);
    }

    const deliverySelect = this.querySelector('[ref="deliverySelect"]');
    if (deliverySelect instanceof HTMLSelectElement) {
      for (const option of deliverySelect.options) {
        const interval = option.dataset.interval || option.textContent || '';
        const localized = localizeSealInterval(interval, i18n);
        option.dataset.interval = localized;
        option.textContent = localized;
      }
    }

    const planCopy = this.querySelector('[ref="subscriptionPlanCopy"]');
    if (planCopy instanceof HTMLElement && planCopy.innerHTML.trim()) {
      planCopy.innerHTML = localizeSealHtml(planCopy.innerHTML, i18n);
    }
  }

  #syncSealSubscriptionMeta() {
    const scope = this.closest('.product-details, [data-testid="product-information-details"], .shopify-section');
    const planId = this.#getActiveSellingPlanId();
    const sealMeta = findSealPlan(readSealProductData(scope), planId);
    const i18n = readSealI18n(this);

    if (!sealMeta) {
      this.#localizeSealBenefits();
      return;
    }

    const { plan, group } = sealMeta;

    const titleEl = this.querySelector('[ref="subscriptionTitle"]');
    if (titleEl instanceof HTMLElement) {
      const rawTitle = String(plan.name || group.name || titleEl.textContent || '').trim();
      if (rawTitle) {
        titleEl.textContent = localizeSealPhrases(rawTitle, i18n);
      }
    }

    const deliveryValue = this.querySelector('[ref="deliveryValue"]');
    const deliveryText = getDeliveryBenefitText(group, plan, i18n);
    if (deliveryValue instanceof HTMLElement && deliveryText) {
      deliveryValue.textContent = deliveryText;
    }

    const planCopy = this.querySelector('[ref="subscriptionPlanCopy"]');
    if (planCopy instanceof HTMLElement && plan.description) {
      const cleaned = sanitizeSubscriptionDescription(String(plan.description));
      const inner = cleaned
        .replace(/<\/?p[^>]*>/gi, '')
        .replace(/^\s*<br\s*\/?>/i, '')
        .trim();
      if (inner && !planCopy.innerHTML.trim()) {
        planCopy.innerHTML = localizeSealHtml(inner, i18n);
      }
    }

    this.#localizeSealBenefits();
  }

  /**
   * @returns {string}
   */
  #getActiveSellingPlanId() {
    const deliverySelect = this.querySelector('[ref="deliverySelect"]');
    if (deliverySelect instanceof HTMLSelectElement && deliverySelect.value) {
      return deliverySelect.value;
    }

    const subInput = this.querySelector('.purchase-options__input[value="subscription"]');
    if (subInput instanceof HTMLInputElement && subInput.dataset.sellingPlanId) {
      return subInput.dataset.sellingPlanId;
    }

    return this.dataset.sellingPlanId || '';
  }

  #onDeliveryChange = () => {
    const deliverySelect = this.querySelector('[ref="deliverySelect"]');
    const subInput = this.querySelector('.purchase-options__input[value="subscription"]');

    if (!(deliverySelect instanceof HTMLSelectElement) || !(subInput instanceof HTMLInputElement)) return;

    subInput.dataset.sellingPlanId = deliverySelect.value;
    this.dataset.sellingPlanId = deliverySelect.value;

    const scope = this.closest('.product-details, [data-testid="product-information-details"], .shopify-section');
    const sealMeta = findSealPlan(readSealProductData(scope), deliverySelect.value);
    const planCopy = this.querySelector('[ref="subscriptionPlanCopy"]');
    const i18n = readSealI18n(this);

    if (planCopy instanceof HTMLElement && sealMeta?.plan.description) {
      const inner = sanitizeSubscriptionDescription(String(sealMeta.plan.description))
        .replace(/<\/?p[^>]*>/gi, '')
        .replace(/^\s*<br\s*\/?>/i, '')
        .trim();
      planCopy.innerHTML = localizeSealHtml(inner, i18n);
    }

    const titleEl = this.querySelector('[ref="subscriptionTitle"]');
    if (titleEl instanceof HTMLElement && sealMeta) {
      const rawTitle = String(sealMeta.plan.name || sealMeta.group.name || '').trim();
      if (rawTitle) {
        titleEl.textContent = localizeSealPhrases(rawTitle, i18n);
      }
    }

    this.#syncSealSubscriptionMeta();
    this.#syncFormState();
  };

  #scheduleSealCleanup() {
    if (this.#sealCleanupScheduled) return;
    this.#sealCleanupScheduled = true;

    const scope = this.closest('.product-details, [data-testid="product-information-details"], .shopify-section');

    const run = () => {
      hideSealSubscriptions(scope);
      hideSealSubscriptions(this.closest('.buy-buttons-block'));
      this.#syncSealSubscriptionMeta();
      this.#syncFormState();
    };

    run();
    requestAnimationFrame(run);
    window.setTimeout(run, 300);
    window.setTimeout(run, 1500);
  }

  /**
   * @param {{ forAcceleratedCheckout?: boolean }} [options]
   */
  #syncFormState(options = {}) {
    const { forAcceleratedCheckout = false } = options;
    const sellingPlanInput = this.#getSellingPlanInput();
    const variantInput = this.#getVariantInput();
    const checked = this.querySelector('.purchase-options__input:checked:not(:disabled)');
    const isSubscription = checked?.value === 'subscription';
    const planId = isSubscription ? this.#getActiveSellingPlanId() : '';
    const scope = this.closest('.product-details, [data-testid="product-information-details"], .shopify-section');
    const buyButtons = this.closest('.buy-buttons-block');

    if (isSubscription && planId) {
      if (sellingPlanInput) {
        sellingPlanInput.setAttribute('name', 'selling_plan');
        sellingPlanInput.value = planId;
        sellingPlanInput.disabled = false;
      }

      const subscriptionVariantId = this.dataset.subscriptionVariantId;
      if (variantInput && subscriptionVariantId) {
        variantInput.value = subscriptionVariantId;
      }
    } else {
      if (sellingPlanInput) {
        sellingPlanInput.value = '';
        sellingPlanInput.removeAttribute('name');
        sellingPlanInput.disabled = false;
      }

      if (variantInput) {
        const nextVariantId = this.#pickerVariantId || this.dataset.purchaseVariantId;
        if (nextVariantId) {
          variantInput.value = nextVariantId;
        }
      }
    }

    const quantitySelector = this.#getProductForm()?.querySelector('quantity-selector-component');
    if (quantitySelector instanceof HTMLElement && variantInput?.value) {
      quantitySelector.setAttribute('data-variant-id', variantInput.value);
    }

    // Buy it now needs #shopify-buyer-consent visible with a selling plan.
    // Add to cart breaks if that consent node is shown — keep it hidden for ATC.
    const showConsent = Boolean(forAcceleratedCheckout && isSubscription && planId);
    setShopifyBuyerConsentVisible(scope, showConsent);
    setShopifyBuyerConsentVisible(buyButtons, showConsent);
  }

  #onFormSubmit = () => {
    const scope = this.closest('.product-details, .shopify-section');
    hideSealSubscriptions(scope);
    this.#syncFormState({ forAcceleratedCheckout: false });
  };

  #onAddToCartClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (!target.closest('.add-to-cart-button, [ref="addToCartButton"]')) return;

    const scope = this.closest('.product-details, .shopify-section');
    hideSealSubscriptions(scope);
    this.#syncFormState({ forAcceleratedCheckout: false });
  };

  #onAcceleratedCheckoutClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (
      !target.closest(
        '.shopify-payment-button, shopify-accelerated-checkout, shopify-buy-it-now-button, [data-shopify="payment-button"]'
      )
    ) {
      return;
    }

    const scope = this.closest('.product-details, .shopify-section');
    hideSealSubscriptions(scope);
    // Reveal Shopify subscription consent only for Buy it now — ATC breaks if it stays visible.
    this.#syncFormState({ forAcceleratedCheckout: true });
  };

  #onChange = () => {
    this.#syncFormState({ forAcceleratedCheckout: false });
  };

  /**
   * @param {import('@theme/events').VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const resource = event.detail.resource;
    if (resource?.id) {
      this.#pickerVariantId = String(resource.id);
    }

    const blockId = this.dataset.blockId;
    const newComponent = event.detail.data.html.querySelector(
      `purchase-options-component[data-block-id="${blockId}"]`
    );

    if (newComponent) {
      morph(this, newComponent);

      if (newComponent.dataset.purchaseVariantId) {
        this.#pickerVariantId = newComponent.dataset.purchaseVariantId;
      }
      if (newComponent.dataset.subscriptionVariantId) {
        this.dataset.subscriptionVariantId = newComponent.dataset.subscriptionVariantId;
      }
    }

    queueMicrotask(() => {
      hideSealSubscriptions(this.closest('.product-details, .shopify-section'));
      this.#syncSealSubscriptionMeta();
      this.#sanitizeRenderedBenefits();
      this.#localizeSealBenefits();
      this.#updatePricesForVariantId(this.#pickerVariantId);
      this.#syncFormState({ forAcceleratedCheckout: false });
    });
  };
}

if (!customElements.get('seal-subscriptions-guard')) {
  customElements.define('seal-subscriptions-guard', SealSubscriptionsGuard);
}

if (!customElements.get('purchase-options-component')) {
  customElements.define('purchase-options-component', PurchaseOptionsComponent);
}
